import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeProvider } from "../theme/ThemeContext";
import { fetchBranding } from "../theme/fetchBranding";

export interface Brand {
  name: string;
  displayName: string;
  env: string;
  domains: string[];
  themeId?: string | null;
}

const DEFAULT_ENVIRONMENT = "staging";

interface DomainContextValue {
  brands: Brand[];
  themes: unknown[];
  /** The brand matched by domain URL. */
  domainMatchedBrand: Brand;
  matchedEnvironment: string;
  isLoading: boolean;
  hasError: boolean;
  /** True when branding returned at least one brand. */
  brandingOk: boolean;
}

const DomainContext = createContext<DomainContextValue | null>(null);

const DEFAULT_BRAND: Brand = {
  name: "default",
  displayName: "Rapid Crews",
  env: DEFAULT_ENVIRONMENT,
  domains: [],
  themeId: null,
};

interface DomainMatchResult {
  brand: Brand;
  environment: string;
  matched: boolean;
}

function asBrand(raw: unknown): Brand | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  if (typeof row.name !== "string" || row.name === "") return null;
  return {
    name: row.name,
    displayName: typeof row.displayName === "string" ? row.displayName : row.name,
    env: typeof row.env === "string" ? row.env : DEFAULT_ENVIRONMENT,
    domains: Array.isArray(row.domains) ? row.domains.filter((item): item is string => typeof item === "string") : [],
    themeId: typeof row.themeId === "string" ? row.themeId : row.themeId === null ? null : undefined,
  };
}

function getBrandForCurrentDomain(brands: Brand[]): DomainMatchResult {
  const currentOrigin = window.location.origin;
  for (const brand of brands) {
    if (brand.domains.includes(currentOrigin)) {
      return { brand, environment: brand.env || DEFAULT_ENVIRONMENT, matched: true };
    }
  }
  const fallbackBrand = brands.find((brand) => brand.name === "default") || brands[0] || DEFAULT_BRAND;
  return {
    brand: fallbackBrand,
    environment: fallbackBrand.env || DEFAULT_ENVIRONMENT,
    matched: false,
  };
}

export function DomainContextProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [brands, setBrands] = useState<Brand[]>([DEFAULT_BRAND]);
  const [themes, setThemes] = useState<unknown[]>([]);
  const [domainMatchedBrand, setDomainMatchedBrand] = useState<Brand>(DEFAULT_BRAND);
  const [matchedEnvironment, setMatchedEnvironment] = useState<string>(DEFAULT_ENVIRONMENT);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [brandingOk, setBrandingOk] = useState(false);

  useEffect(() => {
    const load = async () => {
      const result = await fetchBranding(fetch);
      if (result.ok && result.brands.length > 0) {
        const parsed = result.brands.map(asBrand).filter((brand): brand is Brand => brand !== null);
        if (parsed.length === 0) {
          setHasError(true);
          setBrandingOk(false);
          navigate("/error", { replace: true, state: { message: "No brand configuration available" } });
        } else {
          setBrands(parsed);
          setThemes(result.themes);
          const match = getBrandForCurrentDomain(parsed);
          setDomainMatchedBrand(match.brand);
          setMatchedEnvironment(match.environment);
          setBrandingOk(true);
        }
      } else if (result.ok) {
        console.error("No brands configured in domain config");
        setHasError(true);
        setBrandingOk(false);
        navigate("/error", { replace: true, state: { message: "No brand configuration available" } });
      } else {
        console.error("Failed to fetch domain config:", result.reason);
        setBrandingOk(false);
      }
      setIsLoading(false);
    };
    void load();
  // The error route is the same one this effect has always used.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DomainContext.Provider value={{
      brands,
      themes,
      domainMatchedBrand,
      matchedEnvironment,
      isLoading,
      hasError,
      brandingOk,
    }}>
      <ThemeProvider>{children}</ThemeProvider>
    </DomainContext.Provider>
  );
}

export function useDomain(): DomainContextValue {
  const ctx = useContext(DomainContext);
  if (!ctx) {
    throw new Error("useDomain must be used within a DomainContextProvider");
  }
  return ctx;
}
